import { ChevronRight } from "lucide-react";
import { DateTime } from "luxon";
import { UserAvatar } from "@/components/ui/stacked-avatars";
import { cn } from "@/lib/utils";
import {
  ShareableEntity,
  ShareableEntityMoment,
} from "@/lib/api/shareable-entities";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";

interface MomentCardProps {
  entity: ShareableEntity & { moment: ShareableEntityMoment };
  isNew: boolean;
  onClick?: () => void;
  className?: string;
}

export function MomentCard({
  entity,
  isNew,
  onClick,
  className,
}: MomentCardProps) {
  const isScheduled = entity.status === "scheduled" && entity.scheduledAt;

  const formattedDate = isScheduled
    ? `Scheduled, ${DateTime.fromISO(entity.scheduledAt!).toFormat("MMM d")}`
    : DateTime.fromJSDate(new Date(entity.created_at)).toFormat("EEE, h:mm a");

  const caption = entity.moment.caption ?? "";
  const preview = caption.length > 50 ? `${caption.slice(0, 50)}...` : caption;
  const creatorName = entity.owner.name;
  const creatorAvatarUrl = entity.owner.profile_picture_url ?? undefined;

  const rawThumbnailUrl = entity.moment.images[0]?.thumbnailUrl;
  const { url: thumbnailUrl } = useSignedMedia(rawThumbnailUrl);
  const imageCount = entity.moment.images.length;

  return (
    <div
      onClick={onClick}
      className={cn(
        "flex cursor-pointer items-center gap-4 rounded-lg p-3 transition-colors hover:bg-gray-50",
        className,
      )}
    >
      {/* Stacked tilted photo thumbnails */}
      <div className="relative shrink-0 h-20 w-16">
        {/* Back layer (3rd image) */}
        {imageCount >= 3 && (
          <div
            className="absolute inset-0 rounded-md bg-gray-300 shadow-sm"
            style={{ transform: "rotate(4deg) translateX(4px)" }}
          />
        )}
        {/* Middle layer (2nd image) */}
        {imageCount >= 2 && (
          <div
            className="absolute inset-0 rounded-md bg-gray-200 shadow-sm"
            style={{ transform: "rotate(-2deg) translateX(-2px)" }}
          />
        )}
        {/* Front layer (main image) */}
        {thumbnailUrl ? (
          <div
            className="absolute inset-0 overflow-hidden rounded-md shadow-md"
            style={{ transform: "rotate(-5deg)" }}
          >
            <img
              src={thumbnailUrl}
              alt="Moment thumbnail"
              className="h-full w-full object-cover"
            />
          </div>
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center rounded-md bg-gray-200 shadow-md"
            style={{ transform: "rotate(-5deg)" }}
          >
            <span className="text-[8px] text-gray-500">No image</span>
          </div>
        )}
        {/* Image count badge */}
        {imageCount > 1 && (
          <div
            className="absolute -right-1 -top-1 z-10 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white"
          >
            +{imageCount - 1}
          </div>
        )}
        {/* Avatar overlay */}
        <div className="absolute -bottom-1 -right-1 z-10">
          <UserAvatar
            imageUrl={creatorAvatarUrl}
            name={creatorName}
            size={28}
            showBorder={true}
          />
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="font-bold text-primary">Moments</span>
          <span className="h-1 w-1 rounded-full bg-text-tertiary" />
          <span className="text-sm text-text-tertiary">{formattedDate}</span>
        </div>
        {preview && <p className="truncate text-sm text-tertiary">{preview}</p>}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {isNew && (
          <span className="h-2 w-2 rounded-full bg-purple-600" />
        )}
        <ChevronRight className="h-5 w-5 text-gray-400" />
      </div>
    </div>
  );
}

